import LookupTableProps from 'types/lookupTableProps'
import MessageProps from 'types/MessageProps'

const appName = 'Alairo Solutions Insights'

const brandLogo = 'images/header-logo.svg'
const brandLogoSmall = 'images/header-logo-smaller.svg'
const eulaLocation = 'pdf/EULA.pdf'

const apiBaseUrl =
  process.env.NODE_ENV === 'development'
    ? process.env.REACT_APP_API_URL
    : (window as any).REACT_APP_API_URL

const appPublicUrl =
  process.env.NODE_ENV === 'development'
    ? process.env.REACT_APP_PUBLIC_URL
    : (window as any).REACT_APP_PUBLIC_URL

const apiUrls = {
  centers: 'centers',
  connections: 'connections',
  countries: 'countries',
  customers: 'customers',
  devices: 'devices',
  emailTemplates: 'email-templates',
  filters: 'filters',
  filterValues: 'filter-values',
  filterValuesToPermissions: 'filter-values-to-permissions',
  pages: 'pages',
  permissions: 'permissions',
  permissionsToRoles: 'permissions-to-roles',
  permissionsToSections: 'permissions-to-sections',
  filtersToPages: 'filters-to-pages',
  filtersToReports: 'filters-to-reports',
  pagesToReports: 'pages-to-reports',
  reports: 'reports',
  reportsToFormats: 'reports-to-formats',
  roles: 'roles',
  sections: 'sections',
  sectionsToPages: 'sections-to-pages',
  settings: 'settings',
  states: 'states',
  users: 'users',
  usersToCustomers: 'users-to-customers',
  usersToRoles: 'users-to-roles',
  filterSyncLog: 'filter-sync-log',
  filterSyncLogDetails: 'filter-sync-log-details',
  actionAcceptEula: 'users/accept-eula',
  actionAddAll: 'add-all',
  actionRemoveAll: 'remove-all',
  actionBulkCreate: 'bulk-create',
  actionBulkDelete: 'bulk-delete',
  actionBulkUpdate: 'bulk-update',
  actionEmailNotification: 'email-notification',
  actionCreateOnBackground: 'create-on-background',
  actionGetMenuPages: 'pages/get-menu-pages',
  actionGetAisReports: 'ais/get-reports',
  actionGetAppUrl: 'system/get-app-url',
  actionGetMenuSections: 'sections/get-menu-sections',
  actionGetParentFilters: 'filters/get-parent-filters',
  actionGetVslzToken: 'system/get-vslz-token',
  actionGetVslzReports: 'vslz/get-reports',
  actionGetFilterValues: 'filter-values/get-filter-values',
  actionGetPageFilters: 'filters/get-page-filters',
  actionGetReportFilters: 'filters/get-report-filters',
  actionGetRoles: 'get-roles',
  actionAccount: 'account',
  actionImport: 'import',
  actionSort: 'sort',
  actionSave: 'save',
  actionUpload: 'upload',
  actionChangeCustomer: 'change-customer',
  actionChangeFilterRole: 'change-filter-role',
  actionChangePassword: 'change-password',
  actionMyCustomers: 'my-customers',
  actionExport: 'export',
  actionGetSelectedCustomer: 'get-selected-customer',
  actionGetSelectedFilterRole: 'get-selected-filter-role',
  actionResetPasswordRequest: 'reset-password-request',
  actionResetPassword: 'reset-password',
  actionGetFilter: 'get',
  actionSaveFilter: 'save',
  actionSelect: 'select',
  actionSectionNames: 'section-names',
  actionTemplate: 'template',
  actionSetAsDefault: 'set-default',
  actionUnlock: 'unlock',
  actionSyncFilters: 'sync-filters',
  actionSync: 'sync',
  actionTest: 'test',
}

const visualizeUrl = 'https://vslz.caresight.com/'
const aisUrl = 'https://ais.alairoinsights.com/api/reports'

const externalUrls = {
  visualize: visualizeUrl,
  visualizeReport: visualizeUrl + 'report/show',
  visualizeReportPrint: visualizeUrl + 'report/print',
  visualizeReportPrintCsv: visualizeUrl + 'report/print-csv',
  visualizeReportPrintXls: visualizeUrl + 'report/print-xlsx',
  aisUrl: aisUrl,
  aisDisplayAction: 'display'
}

const loginViewModes = {
  login: 1,
  resetPassword: 2,
  contactUs: 3,
  setNewPassword: 4,
}

const pageSizes = [10, 15, 20, 25, 35, 50]

const defaultPageSize = 10

const defaultAxiosContentType = 'application/json' //application/form-data; charset=UTF-8

const tableColumnWidths = {
  sortColumn: '3rem',
  id: '4rem',
  small: '4rem', 
  mid: '8rem',
  active: '4rem',
  action: '5rem',
  extended_action: '19rem',
}

const emptyMessage: MessageProps = {
  text: '',
  status: '',
  display: '',
}

const lookupTablesData: Array<LookupTableProps> = [
  { appUrl: 'carriers', label: 'Carriers' },
  { appUrl: 'failure-reasons', label: 'Failure reasons' },
  { appUrl: 'manufacturers', label: 'Device Manufacturers' },
  { appUrl: 'models', label: 'Device Models' },
  { appUrl: 'service-statuses', label: 'Service Statuses' },
  { appUrl: 'types', label: 'Device Types' },
]

const roles = {
  admin: 'admin',
  superAdmin: 'super admin',
  user: 'user',
}

const rolesInherit = {
  superAdmin: ['admin', 'user'],
  admin: ['user'],
}

const filterRoles = [
  {
    name: 'User',
    value: 'user',
  },
  {
    name: 'Super Admin',
    value: 'super admin',
  },
]

const statuses = {
  pending: 'pending',
  active: 'active',
}

const iconOptionsList = [
  {
    id: 'bookOutline',
    name: 'Book',
  },
  {
    id: 'browsersOutline',
    name: 'Browsers',
  },
  {
    id: 'desktopOutline',
    name: 'Desktop',
  },
  {
    id: 'funnelOutline',
    name: 'Funnel',
  },
  {
    id: 'gridOutline',
    name: 'Grid',
  },
  {
    id: 'homeOutline',
    name: 'Home',
  },
  {
    id: 'keyOutline',
    name: 'Key',
  },
  {
    id: 'listOutline',
    name: 'List',
  },
  {
    id: 'peopleOutline',
    name: 'People',
  },
  {
    id: 'personCircleOutline',
    name: 'Person Circle',
  },
  {
    id: 'pulseOutline',
    name: 'Pulse',
  },
  {
    id: 'statsChartOutline',
    name: 'Stats Chart',
  },
  {
    id: 'settingsOutline',
    name: 'Settings',
  },
  {
    id: 'trendingUp',
    name: 'Trending Up',
  },
  {
    id: 'tvOutline',
    name: 'TV',
  },
]

const attFormats = [
  {
    id: 'csv',
    name: 'CSV',
  },
  {
    id: 'png',
    name: 'PNG',
  },
  {
    id: 'pdf',
    name: 'PDF',
  },
  {
    id: 'xls',
    name: 'XLS',
  },
]

const settings = {
  pageSize: 'pageSize',
}

const filterValuesTypes = [
  {
    id: null,
    name: 'Text',
  },
  {
    id: 'd',
    name: 'Date',
  },
  {
    id: 'dt',
    name: 'Datetime',
  },
]

const reportHeights = [
  {
    id: '200',
    name: '200',
  },
  {
    id: '280',
    name: '280',
  },
  {
    id: '400',
    name: '400',
  },
  {
    id: '600',
    name: '600',
  },
  {
    id: 'full',
    name: 'Full',
  },
]

const connectionTypes = [
  {
    id: 'mysql',
    name: 'MySQL',
  },
  {
    id: 'sqlsrv',
    name: 'MSSQL',
  },
]


const syncTypes = [
  {
    id: 1,
    name: 'Add Only New',
  },
  {
    id: 2,
    name: 'Replace Synced',
  },
  {
    id: 3,
    name: 'Replace All',
  },
]


export {
  apiBaseUrl,
  apiUrls,
  appName,
  appPublicUrl,
  brandLogo,
  brandLogoSmall,
  defaultAxiosContentType,
  defaultPageSize,
  emptyMessage,
  eulaLocation,
  externalUrls,
  filterRoles,
  lookupTablesData,
  pageSizes,
  roles,
  rolesInherit,
  statuses,
  tableColumnWidths,
  loginViewModes,
  iconOptionsList,
  attFormats,
  settings,
  filterValuesTypes,
  reportHeights,
  connectionTypes,
  syncTypes,
}
